import { ARIA, CLASS_NAMES, EVENTS, SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * HeadlineAndForm — handles custom select dropdown behaviour for the
 * headline-and-form component. Syncs the visual custom dropdown to a hidden
 * native <select> so MC4WP can read the value on form submission.
 * @class HeadlineAndForm
 * @extends MessageBus
 * @description Manages open/close toggling, option selection, chevron state,
 * outside-click dismissal, keyboard navigation, and submit-time validation.
 */
export default class HeadlineAndForm extends MessageBus {
  /**
   * @param {HTMLElement} element - the module's container
   */
  constructor(element) {
    super();

    /** @type {HTMLElement} */
    this.element = element;
    this.isOpen = ARIA.FALSE;

    this.init();
  }

  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  cacheDomReferences() {
    this.formEl = this.element.querySelector(SELECTORS.HEADLINE_AND_FORM_FORM);

    this.selectWrapperEl = this.element.querySelector(
      SELECTORS.HEADLINE_AND_FORM_INQUIRY_TYPE
    );

    this.nativeSelectEl = this.selectWrapperEl?.querySelector(
      SELECTORS.HEADLINE_AND_FORM_NATIVE_SELECT
    );

    this.triggerEl = this.selectWrapperEl?.querySelector(
      SELECTORS.HEADLINE_AND_FORM_TRIGGER
    );

    this.labelEl = this.selectWrapperEl?.querySelector(
      SELECTORS.HEADLINE_AND_FORM_LABEL
    );

    this.optionsListEl = this.selectWrapperEl?.querySelector(
      SELECTORS.HEADLINE_AND_FORM_OPTIONS
    );

    this.optionEls = this.selectWrapperEl
      ? Array.from(
          this.selectWrapperEl.querySelectorAll(
            SELECTORS.HEADLINE_AND_FORM_OPTION
          )
        )
      : [];

    this.submitEl = this.formEl?.querySelector(
      SELECTORS.HEADLINE_AND_FORM_SUBMIT
    );

    this.requiredInputEls = [
      document.getElementById(SELECTORS.HEADLINE_AND_FORM_NAME_FIELD),
      document.getElementById(SELECTORS.HEADLINE_AND_FORM_EMAIL_FIELD),
      document.getElementById(SELECTORS.HEADLINE_AND_FORM_ORGANIZATION_FIELD)
    ].filter(Boolean);

    return this;
  }

  setupHandlers() {
    this.handleTriggerClick = this.onTriggerClick.bind(this);
    this.handleTriggerKeydown = this.onTriggerKeydown.bind(this);
    this.handleOptionClick = this.onOptionClick.bind(this);
    this.handleOptionKeydown = this.onOptionKeydown.bind(this);
    this.handleDocumentClick = this.onDocumentClick.bind(this);
    this.handleFormSubmit = this.onFormSubmit.bind(this);

    return this;
  }

  enable() {
    if (!this.selectWrapperEl || !this.triggerEl || !this.optionsListEl) {
      return this;
    }

    if (this.submitEl) {
      this.submitEl.onclick = (e) => {
        this.onFormSubmit(e);
      };
    }

    this.closeDropdown();
    this.bindEvents();

    return this;
  }

  destroy() {
    this.triggerEl?.removeEventListener(EVENTS.CLICK, this.handleTriggerClick);
    this.triggerEl?.removeEventListener(
      EVENTS.KEY_DOWN,
      this.handleTriggerKeydown
    );

    this.optionEls.forEach((option) => {
      option.removeEventListener(EVENTS.CLICK, this.handleOptionClick);
      option.removeEventListener(EVENTS.KEY_DOWN, this.handleOptionKeydown);
    });

    document.removeEventListener(EVENTS.CLICK, this.handleDocumentClick);
    this.formEl?.removeEventListener(EVENTS.SUBMIT, this.handleFormSubmit);

    return this;
  }

  /**
   * Opens the custom dropdown and updates ARIA attributes.
   * @returns {this} The instance for chaining.
   */
  openDropdown() {
    this.isOpen = ARIA.TRUE;
    this.selectWrapperEl.classList.add(CLASS_NAMES.IS_OPEN);
    this.triggerEl.setAttribute(ARIA.EXPANDED, ARIA.TRUE);
    this.optionsListEl.removeAttribute(ARIA.HIDDEN);
    this.optionsListEl.classList.add(CLASS_NAMES.SELECT_OPEN);
    this.triggerEl.classList.add(CLASS_NAMES.SELECT_OPEN);

    return this;
  }

  /**
   * Closes the custom dropdown and updates ARIA attributes.
   * @returns {this} The instance for chaining.
   */
  closeDropdown() {
    this.isOpen = ARIA.FALSE;
    this.selectWrapperEl.classList.remove(CLASS_NAMES.IS_OPEN);
    this.triggerEl?.setAttribute(ARIA.EXPANDED, ARIA.FALSE);
    this.optionsListEl?.setAttribute(ARIA.HIDDEN, ARIA.TRUE);
    this.optionsListEl?.classList.remove(CLASS_NAMES.SELECT_OPEN);
    this.triggerEl?.classList.remove(CLASS_NAMES.SELECT_OPEN);

    return this;
  }

  /**
   * Selects an option: updates the visual label, syncs the native select,
   * marks aria-selected, clears any validation error, and closes the dropdown.
   * @param {HTMLElement} option - The option element that was chosen.
   * @returns {this} The instance for chaining.
   */
  selectOption(option) {
    const { value } = option.dataset;

    this.labelEl.textContent = value;
    this.labelEl.classList.add(CLASS_NAMES.HAS_VALUE);

    this.nativeSelectEl.value = value;
    this.nativeSelectEl.dispatchEvent(
      new Event(EVENTS.CHANGE, { bubbles: true })
    );

    this.optionEls.forEach((o) => o.setAttribute(ARIA.SELECTED, ARIA.FALSE));
    option.setAttribute(ARIA.SELECTED, ARIA.TRUE);

    this.selectWrapperEl.classList.remove(CLASS_NAMES.HAS_ERROR);
    this.selectWrapperEl.classList.remove(CLASS_NAMES.INPUT_ERROR);

    this.closeDropdown();

    return this;
  }

  /**
   * Toggle dropdown open/closed on trigger click.
   * @param {MouseEvent} event - The click event on the trigger element.
   */
  onTriggerClick(event) {
    event.stopPropagation();

    if (this.isOpen === ARIA.TRUE) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  /**
   * Handle keyboard interaction on the trigger (Enter, Space, Escape).
   * @param {KeyboardEvent} event - The keydown event on the trigger element.
   */
  onTriggerKeydown(event) {
    if (event.key === EVENTS.KEY_ENTER || event.key === EVENTS.KEY_SPACE) {
      event.preventDefault();
      if (this.isOpen === ARIA.TRUE) {
        this.closeDropdown();
      } else {
        this.openDropdown();
      }
    }

    if (event.key === EVENTS.KEY_ESCAPE) {
      this.closeDropdown();
    }
  }

  /**
   * Select an option on click.
   * @param {MouseEvent} event - The click event on an option element.
   */
  onOptionClick(event) {
    this.selectOption(event.currentTarget);
  }

  /**
   * Select an option via keyboard (Enter or Space).
   * @param {KeyboardEvent} event - The keydown event on an option element.
   */
  onOptionKeydown(event) {
    if (event.key === EVENTS.KEY_ENTER || event.key === EVENTS.KEY_SPACE) {
      event.preventDefault();
      this.selectOption(event.currentTarget);
    }
  }

  /**
   * Close dropdown when a click is detected outside the wrapper.
   * @param {MouseEvent} event - The click event on the document.
   */
  onDocumentClick(event) {
    if (!this.selectWrapperEl.contains(event.target)) {
      this.closeDropdown();
    }
  }

  /**
   * Validate the custom select on form submit. Prevents submission and marks
   * an error state if the field is required but no value has been chosen.
   * @param {SubmitEvent} event - The submit event on the form element.
   */
  onFormSubmit(event) {
    const responseEl = document.querySelector(SELECTORS.MAILCHIMP_RESPONSE);
    const isRequired = this.selectWrapperEl?.dataset.required === 'true';
    let hasError = false;

    if (responseEl) {
      responseEl.innerHTML = '';
    }

    this.requiredInputEls.forEach((input) => {
      if (!input.value.trim()) {
        input.classList.add(CLASS_NAMES.INPUT_ERROR);
        hasError = true;
      } else {
        input.classList.remove(CLASS_NAMES.INPUT_ERROR);
      }
    });

    if (isRequired && !this.nativeSelectEl.value) {
      this.selectWrapperEl.classList.add(CLASS_NAMES.INPUT_ERROR);
      this.triggerEl.focus();
      hasError = true;
    }

    if (hasError) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  bindEvents() {
    this.triggerEl.addEventListener(EVENTS.CLICK, this.handleTriggerClick);
    this.triggerEl.addEventListener(EVENTS.KEY_DOWN, this.handleTriggerKeydown);

    this.optionEls.forEach((option) => {
      option.addEventListener(EVENTS.CLICK, this.handleOptionClick);
      option.addEventListener(EVENTS.KEY_DOWN, this.handleOptionKeydown);
    });

    document.addEventListener(EVENTS.CLICK, this.handleDocumentClick);
    this.formEl?.addEventListener(EVENTS.SUBMIT, this.handleFormSubmit);

    return this;
  }
}
